import { useEffect, useRef, useState } from "react";
import { formatDateOnly } from "@/lib/format-date-only";
import { cn } from "@/lib/utils";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { AlertTriangle, Calendar, Car, Check, ChevronDown, ChevronUp, Copy, DollarSign, Thermometer, ExternalLink, FileText, Globe, Home, KeyRound, Link2, Lock, Loader2, Mail, Phone, Send, Star, User, Video, Wifi, X } from "lucide-react";
import { QuoCallButton } from "./QuoCallButton";
import { Link } from "wouter";
import type { Member, Property } from "@shared/schema";
import { getMemberRating } from "@shared/member-display";
import { avatarColor, initials } from "./_shared";
import { TruncatedText } from "@/components/truncated-text";
import { getOutageUrl, extractZipFromAddress } from "@/lib/isp-utils";
import {
  UNRESOLVED_Q_KEY,
  memberUnresolvedCountQueryKey,
  useResolveInvalidations,
  applyOptimisticInboxResolve,
  rollbackOptimisticInboxResolve,
  resolveAllInboxRowUpdate,
  incrementInboxRowUpdate,
} from "./_resolve-invalidations";



// ─── Member Info Sidebar ────────────────────────────────────────────────────
export type SidebarMember = Member & {
  roomNumber?: string | null;
  currentPropertyId?: number | null;
  doorPin?: string | null;
  lockDeviceId?: string | null;
  lockLastUsedAt?: string | null;
  // One entry per lock the member can open (multi-lock rooms). The flat
  // doorPin/lockDeviceId/lockLastUsedAt fields above mirror locks[0]; older
  // payloads omit `locks`, so the UI synthesizes a single lock from them.
  locks?: Array<{
    deviceId: string | null;
    pin: string | null;
    lastUsedAt: string | null;
    codeName: string | null;
  }>;
};


export interface ThermostatSnapshot {
  deviceId: string;
  displayName: string | null;
  recordedAt: string | null;
  temperatureF: number | null;
}


export function ThermostatSidebarRows({ propertyId }: { propertyId: number }) {
  const snapshotsQuery = useQuery<{ snapshots: ThermostatSnapshot[] }>({
    queryKey: ["/api/app/properties", propertyId, "thermostat-snapshots"],
    queryFn: async () => {
      const res = await fetch(`/api/app/properties/${propertyId}/thermostat-snapshots`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load thermostat snapshots");
      return res.json();
    },
  });

  const snapshots = snapshotsQuery.data?.snapshots ?? [];
  // While loading or when no thermostats are assigned: render nothing (no flash, no empty state).
  if (snapshotsQuery.isLoading || snapshots.length === 0) return null;

  return (
    <>
      {snapshots.map((snap) => {
        // Use custom name; fall back to a short device ID suffix so multiple un-named
        // thermostats on the same property remain distinguishable.
        const label =
          snap.displayName ||
          `Thermostat …${snap.deviceId.slice(-4)}`;
        const tempDisplay =
          snap.temperatureF != null
            ? `${Math.round(snap.temperatureF)}°F`
            : null;
        const href = `/app/properties/${propertyId}?tab=devices`;
        return (
          <div
            key={snap.deviceId}
            className="flex items-start gap-2 text-xs"
            data-testid={`text-sidebar-thermostat-${snap.deviceId}`}
          >
            <div className="mt-0.5 shrink-0">
              <Thermometer className="h-3.5 w-3.5 text-gray-400" />
            </div>
            <TruncatedText text={label} className="w-20 shrink-0 text-gray-500" />
            {tempDisplay != null ? (
              <Link
                href={href}
                className="min-w-0 flex-1 break-words text-blue-600 hover:underline"
                data-testid={`link-sidebar-thermostat-${snap.deviceId}`}
              >
                {tempDisplay}
              </Link>
            ) : (
              <Link
                href={href}
                className="min-w-0 flex-1 break-words text-gray-400 hover:underline"
                data-testid={`link-sidebar-thermostat-${snap.deviceId}`}
              >
                No reading
              </Link>
            )}
          </div>
        );
      })}
    </>
  );
}


export function SidebarSection({
  title,
  icon,
  testId,
  children,
  collapsible = false,
  defaultOpen = true,
}: {
  title: string;
  icon: React.ReactNode;
  testId: string;
  children: React.ReactNode;
  // When true the header becomes a toggle button with a chevron, letting the
  // host collapse less-used sections (Contact, Vehicle, Emergency contact).
  collapsible?: boolean;
  // Starting open/closed state for collapsible sections. Ignored when the
  // section isn't collapsible (always rendered open).
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const isOpen = collapsible ? open : true;
  return (
    <div className="rounded-lg border border-gray-200 p-3" data-testid={testId}>
      {collapsible ? (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={isOpen}
          className={`flex w-full items-center gap-1.5 ${isOpen ? "mb-2 border-b border-gray-100 pb-1.5" : ""}`}
          data-testid={`${testId}-toggle`}
        >
          {icon}
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-600">{title}</h3>
          {isOpen ? (
            <ChevronUp className="ml-auto h-3.5 w-3.5 text-gray-400" />
          ) : (
            <ChevronDown className="ml-auto h-3.5 w-3.5 text-gray-400" />
          )}
        </button>
      ) : (
        <div className="mb-2 flex items-center gap-1.5 border-b border-gray-100 pb-1.5">
          {icon}
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-600">{title}</h3>
        </div>
      )}
      {isOpen && <div className="space-y-1.5">{children}</div>}
    </div>
  );
}


export function SidebarRow({
  icon,
  label,
  value,
  testId,
  href,
  external,
  mono,
  emptyText = "None on file",
}: {
  icon: React.ReactNode;
  label: string;
  value: string | null;
  testId: string;
  href?: string | null;
  external?: boolean;
  mono?: boolean;
  // Placeholder rendered when `value` is empty. Defaults to "None on file";
  // pass "" to leave the row visually blank (e.g. a not-yet-scheduled Move-out).
  emptyText?: string;
}) {
  const hasValue = !!(value && String(value).trim());
  const valueClass = `min-w-0 flex-1 break-words ${mono ? "font-mono" : ""} ${
    hasValue ? "text-gray-900" : "text-gray-400 italic"
  }`;
  const linkClass = `min-w-0 flex-1 break-words text-blue-600 hover:underline ${
    mono ? "font-mono" : ""
  }`;
  return (
    <div className="flex items-start gap-2 text-xs">
      <div className="mt-0.5 shrink-0">{icon}</div>
      <span className="w-20 shrink-0 text-gray-500">{label}</span>
      {hasValue && href ? (
        external ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
            data-testid={testId}
          >
            {value}
          </a>
        ) : (
          <Link href={href} className={linkClass} data-testid={testId}>
            {value!}
          </Link>
        )
      ) : (
        <span className={valueClass} data-testid={testId}>
          {hasValue ? value : emptyText}
        </span>
      )}
    </div>
  );
}


function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

// Lock "last used" stamp without the year, with the time of day — e.g.
// "June 16th, 6pm" (minutes shown only when non-zero, am/pm lower-cased).
function formatLockUsed(d: Date): string {
  if (Number.isNaN(d.getTime())) return "--";
  const month = d.toLocaleDateString("en-US", { month: "long" });
  const day = ordinal(d.getDate());
  let hour = d.getHours();
  const min = d.getMinutes();
  const ampm = hour >= 12 ? "pm" : "am";
  hour = hour % 12 || 12;
  const time = min === 0 ? `${hour}${ampm}` : `${hour}:${String(min).padStart(2, "0")}${ampm}`;
  return `${month} ${day}, ${time}`;
}

// Single Wi-Fi row showing "SSID / password" with a one-click copy button so
// the host can paste both to a member. Replaces the prior two separate rows.
function WifiRow({ ssid, password }: { ssid: string | null; password: string | null }) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    },
    [],
  );
  const ssidText = ssid && ssid.trim() ? ssid.trim() : null;
  const pwdText = password && password.trim() ? password.trim() : null;
  const display = `${ssidText ?? "--"} / ${pwdText ?? "--"}`;
  const copyValue = [ssidText, pwdText].filter(Boolean).join(" / ");
  const handleCopy = async () => {
    if (!copyValue) return;
    try {
      await navigator.clipboard.writeText(copyValue);
      setCopied(true);
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setCopied(false), 1500);
      toast({ title: "Wi-Fi copied" });
    } catch {
      toast({ title: "Couldn't copy", description: "Copy the Wi-Fi details manually.", variant: "destructive" });
    }
  };
  return (
    <div className="flex items-start gap-2 text-xs">
      <div className="mt-0.5 shrink-0">
        <Wifi className="h-3.5 w-3.5 text-gray-400" />
      </div>
      <span className="w-20 shrink-0 text-gray-500">Wi-Fi</span>
      <span className="min-w-0 flex-1 break-words font-mono text-gray-900" data-testid="text-sidebar-wifi">
        {display}
      </span>
      <button
        type="button"
        onClick={handleCopy}
        disabled={!copyValue}
        className="mt-0.5 shrink-0 text-gray-400 hover:text-gray-700 disabled:opacity-40"
        data-testid="button-copy-wifi"
        aria-label="Copy Wi-Fi name and password"
        title="Copy Wi-Fi name and password"
      >
        {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

function IspOutageRow({ ispProvider, zipCode }: { ispProvider: string | null; zipCode?: string | null }) {
  if (!ispProvider) return null;
  const url = getOutageUrl(ispProvider, zipCode);
  return (
    <div className="flex items-start gap-2 text-xs">
      <div className="mt-0.5 shrink-0">
        <Globe className="h-3.5 w-3.5 text-gray-400" />
      </div>
      <span className="w-20 shrink-0 text-gray-500">ISP</span>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="min-w-0 flex-1 truncate text-brand hover:underline"
        data-testid="link-isp-outage-check"
        title={`Check ${ispProvider} outage status`}
      >
        Check for outage
      </a>
    </div>
  );
}

export function MemberInfoSidebar({
  memberId,
  fallbackName,
  fallbackPropertyName,
  onClose,
  className,
}: {
  memberId: number;
  fallbackName: string | null;
  fallbackPropertyName: string | null;
  onClose: () => void;
  className?: string;
}) {
  const queryClient = useQueryClient();
  const asideRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Track viewport to scope drawer-only behavior to small screens.
  const [isLg, setIsLg] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 1024px)").matches
      : true,
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = (e: MediaQueryListEvent) => setIsLg(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // Escape key closes the panel (small screens only — on lg it's a docked column).
  useEffect(() => {
    if (isLg) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isLg]);

  // Focus trap + initial focus on small screens. Returns focus to the
  // previously-focused element via the parent's onClose handler.
  useEffect(() => {
    if (isLg) return;
    const node = asideRef.current;
    if (!node) return;

    const getFocusable = (): HTMLElement[] => {
      const sel = [
        "a[href]",
        "button:not([disabled])",
        "input:not([disabled])",
        "select:not([disabled])",
        "textarea:not([disabled])",
        '[tabindex]:not([tabindex="-1"])',
      ].join(",");
      return Array.from(node.querySelectorAll<HTMLElement>(sel)).filter(
        (el) => !el.hasAttribute("disabled") && el.offsetParent !== null,
      );
    };

    // Move focus into the panel on open.
    const focusables = getFocusable();
    (focusables[0] ?? node).focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "Tab") return;
      const items = getFocusable();
      if (items.length === 0) {
        e.preventDefault();
        node?.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !node?.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    node.addEventListener("keydown", onKeyDown);
    return () => node.removeEventListener("keydown", onKeyDown);
  }, [isLg]);

  // Swipe-right-to-close on small screens.
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const touchActive = useRef(false);
  const SWIPE_THRESHOLD_PX = 60;
  const SWIPE_VERTICAL_TOLERANCE_PX = 40;

  function handleTouchStart(e: React.TouchEvent<HTMLElement>) {
    if (isLg) return;
    if (e.touches.length !== 1) return;
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    touchActive.current = true;
  }
  function handleTouchMove(e: React.TouchEvent<HTMLElement>) {
    if (!touchActive.current || isLg) return;
    const sx = touchStartX.current;
    const sy = touchStartY.current;
    if (sx == null || sy == null) return;
    const dx = e.touches[0].clientX - sx;
    const dy = Math.abs(e.touches[0].clientY - sy);
    if (dy > SWIPE_VERTICAL_TOLERANCE_PX && dy > Math.abs(dx)) {
      // Mostly vertical movement — let it scroll, abort swipe gesture.
      touchActive.current = false;
    }
  }
  function handleTouchEnd(e: React.TouchEvent<HTMLElement>) {
    if (!touchActive.current || isLg) {
      touchActive.current = false;
      touchStartX.current = null;
      touchStartY.current = null;
      return;
    }
    const sx = touchStartX.current;
    const sy = touchStartY.current;
    touchActive.current = false;
    touchStartX.current = null;
    touchStartY.current = null;
    if (sx == null || sy == null) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - sx;
    const dy = Math.abs(t.clientY - sy);
    if (dx > SWIPE_THRESHOLD_PX && dy < SWIPE_VERTICAL_TOLERANCE_PX) {
      onCloseRef.current();
    }
  }

  const memberQuery = useQuery<SidebarMember>({
    queryKey: ["/api/app/members", memberId],
    queryFn: async () => {
      const res = await fetch(`/api/app/members/${memberId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load member");
      return res.json();
    },
  });
  const propertiesQuery = useQuery<Property[]>({ queryKey: ["/api/app/properties"] });

  // Task #3086 / #4151 — surface how many of this member's messages are still
  // open. Sourced from the dedicated uncapped per-member count endpoint (NOT the
  // capped-at-8 `/api/app/messages/unresolved` queue), so the badge is correct
  // even when a host has more than 8 open conversations. Resolving/unresolving
  // from any surface invalidates this key, so the count updates in real time.
  const unresolvedQuery = useQuery<{ count: number }>({
    queryKey: memberUnresolvedCountQueryKey(memberId),
  });
  const unresolvedCount = unresolvedQuery.data?.count ?? 0;

  // Task #3088 — "Resolve all" from the sidebar. Reuses the thread's resolve-all
  // endpoint and optimistically clears this member from the shared unresolved
  // queue so the badge above disappears in real time.
  const { toast: sidebarToast } = useToast();
  const { invalidateAfterResolveAll, invalidateAfterUnresolveAll } = useResolveInvalidations(memberId);
  // Task #3096 — re-opens exactly the messages a "Resolve all" just resolved, so
  // an accidental bulk-resolve can be reversed inline from the success toast.
  const sidebarUnresolveAllMutation = useMutation({
    mutationFn: (ids: number[]) =>
      apiRequest("POST", `/api/app/members/${memberId}/messages/unresolve`, { ids }).then((r) =>
        r.json(),
      ),
    onMutate: async (ids: number[]) => {
      // Task #4166 — undoing a bulk resolve re-opens `ids.length` messages, so
      // bump the conversation-list "N open" badge back up immediately (the
      // refetch backfills the oldest-open age hint).
      const { prevInbox } = await applyOptimisticInboxResolve(
        queryClient,
        memberId,
        incrementInboxRowUpdate(ids.length),
      );
      return { prevInbox };
    },
    onSuccess: () => {
      invalidateAfterUnresolveAll();
      sidebarToast({ title: "Messages re-opened" });
    },
    onError: (_err, _ids, ctx) => {
      rollbackOptimisticInboxResolve(queryClient, ctx?.prevInbox);
      sidebarToast({ title: "Could not undo resolve", variant: "destructive" });
    },
  });
  const sidebarResolveAllMutation = useMutation({
    mutationFn: () =>
      apiRequest("POST", `/api/app/members/${memberId}/messages/resolve-all`, {}).then(
        (r) => r.json() as Promise<{ count: number; ids: number[] }>,
      ),
    onMutate: async () => {
      const countKey = memberUnresolvedCountQueryKey(memberId);
      await Promise.all([
        queryClient.cancelQueries({ queryKey: UNRESOLVED_Q_KEY }),
        queryClient.cancelQueries({ queryKey: countKey }),
      ]);
      const prev = queryClient.getQueryData<Array<{ memberId: number }>>(UNRESOLVED_Q_KEY);
      const prevCount = queryClient.getQueryData<{ count: number }>(countKey);
      queryClient.setQueryData<Array<{ memberId: number }>>(UNRESOLVED_Q_KEY, (old) =>
        old ? old.filter((g) => g.memberId !== memberId) : old,
      );
      // Badge reads the per-member count query — clear it optimistically too so
      // the "N open" badge disappears immediately on resolve-all.
      queryClient.setQueryData<{ count: number }>(countKey, { count: 0 });
      // Task #4166 — also clear the conversation-list row's open count so its
      // badge doesn't lag this already-optimistic sidebar badge.
      const { prevInbox } = await applyOptimisticInboxResolve(
        queryClient,
        memberId,
        resolveAllInboxRowUpdate,
      );
      return { prev, prevCount, prevInbox };
    },
    onSuccess: (data) => {
      invalidateAfterResolveAll();
      const resolvedIds = data?.ids ?? [];
      sidebarToast({
        title: "Messages marked as resolved",
        action:
          resolvedIds.length > 0 ? (
            <ToastAction
              altText="Undo resolve all"
              onClick={() => sidebarUnresolveAllMutation.mutate(resolvedIds)}
              data-testid="button-undo-sidebar-resolve-all"
            >
              Undo
            </ToastAction>
          ) : undefined,
      });
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(UNRESOLVED_Q_KEY, ctx.prev);
      if (ctx?.prevCount)
        queryClient.setQueryData(memberUnresolvedCountQueryKey(memberId), ctx.prevCount);
      rollbackOptimisticInboxResolve(queryClient, ctx?.prevInbox);
      sidebarToast({
        title: "Could not resolve messages",
        description: parseErrorMessage(err, "Couldn't resolve messages. Please try again."),
        variant: "destructive",
      });
    },
  });

  const member = memberQuery.data ?? null;
  const displayName =
    (member?.displayName && String(member.displayName).trim()) ||
    (member?.name && String(member.name).trim()) ||
    fallbackName ||
    `Member #${memberId}`;

  const propertyId = member?.currentPropertyId ?? null;
  const propertyFromList = propertyId
    ? (propertiesQuery.data ?? []).find((p) => p.id === propertyId)
    : null;
  const propertyName =
    propertyFromList?.name || propertyFromList?.address || fallbackPropertyName || null;

  const moveInDate = formatDateOnly(member?.moveInDate);

  const moveOutDate = formatDateOnly(member?.moveOutDate);

  // Real `rating` only (no average_rating fallback) — see getMemberRating.
  const ratingValue = getMemberRating(member);
  const memberRating = ratingValue !== null ? ratingValue.toFixed(1) : null;

  const currentDuesDisplay =
    member?.currentDues != null && String(member.currentDues).trim() !== ""
      ? `$${Number(member.currentDues).toFixed(2)}`
      : null;

  const vehicleLine =
    member && (member.carColor || member.carYear || member.carMake || member.carModel)
      ? [member.carColor, member.carYear, member.carMake, member.carModel]
          .filter((v) => v && String(v).trim())
          .join(" ")
          .trim()
      : "";
  const plateLine =
    member && (member.licensePlateState || member.licensePlate)
      ? [member.licensePlateState, member.licensePlate]
          .filter((v) => v && String(v).trim())
          .join(" ")
          .trim()
      : "";

  return (
    <>
      {!isLg && (
        <div
          className="absolute inset-0 z-10 bg-black/40 lg:hidden"
          onClick={() => onCloseRef.current()}
          aria-hidden="true"
          data-testid="backdrop-member-sidebar"
        />
      )}
      <aside
        ref={asideRef}
        id="member-info-sidebar"
        role={isLg ? undefined : "dialog"}
        aria-modal={isLg ? undefined : true}
        aria-label="Member info"
        tabIndex={-1}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className={cn(
          "absolute inset-y-0 right-0 z-20 flex w-80 max-w-[85vw] shrink-0 flex-col overflow-y-auto border-l border-gray-200 bg-white shadow-lg outline-none lg:static lg:z-auto lg:max-w-none lg:shadow-none lg:border-l-0",
          className,
        )}
        data-testid="sidebar-member-info"
      >
      <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white ${avatarColor(displayName)}`}
        >
          {initials(displayName)}
        </div>
        <div className="min-w-0 flex-1">
          <TruncatedText
            as="div"
            text={displayName}
            className="text-sm font-semibold text-gray-900"
            data-testid="text-sidebar-member-name"
          />
          <div className="flex items-center gap-2">
            <Link
              href={`/app/members/${memberId}`}
              className="text-xs text-blue-600 hover:underline"
              data-testid="link-sidebar-full-profile"
            >
              View full profile
            </Link>
            {unresolvedCount > 0 && (
              <Badge
                variant="destructive"
                className="h-4 shrink-0 px-1.5 text-[10px] leading-none"
                data-testid="badge-sidebar-unresolved"
              >
                {unresolvedCount} unresolved
              </Badge>
            )}
            {unresolvedCount > 0 && (
              <button
                type="button"
                className="shrink-0 text-xs font-medium text-green-700 hover:text-green-800 hover:underline disabled:opacity-50"
                onClick={() => sidebarResolveAllMutation.mutate()}
                disabled={sidebarResolveAllMutation.isPending}
                data-testid="button-sidebar-resolve-all"
                title={`Resolve all ${unresolvedCount} unresolved messages`}
              >
                {sidebarResolveAllMutation.isPending ? "Resolving…" : "Resolve all"}
              </button>
            )}
          </div>
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 w-7 p-0 text-gray-500"
          onClick={onClose}
          data-testid="button-close-member-sidebar"
          title="Hide member info"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {memberQuery.isLoading ? (
        <div className="space-y-3 p-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : memberQuery.isError ? (
        <div className="p-4 text-sm text-red-600" data-testid="text-sidebar-error">
          Couldn't load member details. Open the full profile to view this member.
        </div>
      ) : (
        <div className="space-y-3 p-4" data-testid="content-member-sidebar">
          {/* Contact */}
          <SidebarSection
            title="Contact"
            icon={<Mail className="h-4 w-4 text-gray-500" />}
            testId="section-sidebar-contact"
            collapsible
            defaultOpen={false}
          >
            <SidebarRow
              icon={<Mail className="h-3.5 w-3.5 text-gray-400" />}
              label="Email"
              value={member?.email || null}
              testId="text-sidebar-email"
            />
            <SidebarRow
              icon={<Phone className="h-3.5 w-3.5 text-gray-400" />}
              label="Phone"
              value={member?.phone || null}
              testId="text-sidebar-phone"
            />
            {/* Quo messaging — call this member from the connected Quo inbox. */}
            <div className="mt-1 pl-5">
              <QuoCallButton memberId={memberId} memberName={member?.name ?? fallbackName} compact />
            </div>
          </SidebarSection>

          {/* Housing */}
          <SidebarSection
            title="Housing"
            icon={<Home className="h-4 w-4 text-gray-500" />}
            testId="section-sidebar-housing"
          >
            <SidebarRow
              icon={<Home className="h-3.5 w-3.5 text-gray-400" />}
              label="Property"
              value={propertyName}
              testId="text-sidebar-property"
              href={propertyId ? `/app/properties/${propertyId}` : null}
            />
            <SidebarRow
              icon={<Home className="h-3.5 w-3.5 text-gray-400" />}
              label="Room"
              value={member?.roomNumber ? (/^room\s/i.test(member.roomNumber) ? member.roomNumber : `Room ${member.roomNumber}`) : null}
              testId="text-sidebar-room"
              href={propertyId ? `/app/properties/${propertyId}?tab=rooms` : null}
            />
            <SidebarRow
              icon={<Calendar className="h-3.5 w-3.5 text-gray-400" />}
              label="Move-in"
              value={moveInDate}
              testId="text-sidebar-movein"
            />
            <SidebarRow
              icon={<Calendar className="h-3.5 w-3.5 text-gray-400" />}
              label="Move-out"
              value={moveOutDate || null}
              testId="text-sidebar-moveout"
              emptyText=""
            />
            {memberRating && (
              <SidebarRow
                icon={<Star className="h-3.5 w-3.5 text-gray-400" />}
                label="Rating"
                value={memberRating}
                testId="text-sidebar-rating"
              />
            )}
            {currentDuesDisplay && (
              <SidebarRow
                icon={<DollarSign className="h-3.5 w-3.5 text-gray-400" />}
                label="Current dues"
                value={currentDuesDisplay}
                testId="text-sidebar-current-dues"
              />
            )}
            {propertyFromList?.floorPlanUrl && (
              <SidebarRow
                icon={<FileText className="h-3.5 w-3.5 text-gray-400" />}
                label="Floor plan"
                value="View floor plan"
                testId="link-sidebar-floor-plan"
                href={propertyFromList.floorPlanUrl}
                external
              />
            )}
            {propertyFromList?.tourUrl && (
              <SidebarRow
                icon={<Video className="h-3.5 w-3.5 text-gray-400" />}
                label="3D tour"
                value="View 3D tour"
                testId="link-sidebar-3d-tour"
                href={propertyFromList.tourUrl}
                external
              />
            )}
            {(propertyFromList?.wifiName || propertyFromList?.wifiPassword) && (
              <WifiRow
                ssid={propertyFromList?.wifiName ?? null}
                password={propertyFromList?.wifiPassword ?? null}
              />
            )}
            <IspOutageRow
              ispProvider={propertyFromList?.ispProvider ?? null}
              zipCode={propertyFromList?.postalCode ?? extractZipFromAddress(propertyFromList?.address)}
            />
            {propertyId && (() => {
              // One row per lock the member can open (multi-lock rooms, e.g. an
              // interior lock + a private entrance). Synthesize a single lock
              // from the flat fields when the payload predates `locks`.
              const lockList =
                member?.locks && member.locks.length > 0
                  ? member.locks
                  : [
                      {
                        deviceId: member?.lockDeviceId ?? null,
                        pin: member?.doorPin ?? null,
                        lastUsedAt: member?.lockLastUsedAt ?? null,
                        codeName: null as string | null,
                      },
                    ];
              const multi = lockList.length > 1;
              return lockList.map((lk, i) => {
                const used = lk.lastUsedAt ? formatLockUsed(new Date(lk.lastUsedAt)) : "--";
                return (
                  <SidebarRow
                    key={lk.deviceId ?? i}
                    icon={<Lock className="h-3.5 w-3.5 text-gray-400" />}
                    label={multi ? lk.codeName || "Lock" : "Locks"}
                    value={`${lk.pin || "--"} / ${used}`}
                    testId={multi ? `link-sidebar-smart-locks-${i}` : "link-sidebar-smart-locks"}
                    href={
                      lk.deviceId
                        ? `/app/devices?tab=members&openLock=${encodeURIComponent(lk.deviceId)}&lockTab=activity`
                        : `/app/devices?tab=members`
                    }
                    mono={!!lk.pin}
                  />
                );
              });
            })()}
            {propertyId && <ThermostatSidebarRows propertyId={propertyId} />}
          </SidebarSection>

          {/* Vehicle */}
          <SidebarSection
            title="Vehicle"
            icon={<Car className="h-4 w-4 text-gray-500" />}
            testId="section-sidebar-vehicle"
            collapsible
            defaultOpen={false}
          >
            <SidebarRow
              icon={<Car className="h-3.5 w-3.5 text-gray-400" />}
              label="Vehicle"
              value={vehicleLine || null}
              testId="text-sidebar-vehicle"
            />
            <SidebarRow
              icon={<Car className="h-3.5 w-3.5 text-gray-400" />}
              label="Plate"
              value={plateLine || null}
              testId="text-sidebar-plate"
            />
          </SidebarSection>

          {/* Emergency contact */}
          <SidebarSection
            title="Emergency Contact"
            icon={<AlertTriangle className="h-4 w-4 text-gray-500" />}
            testId="section-sidebar-emergency"
            collapsible
            defaultOpen={false}
          >
            <SidebarRow
              icon={<User className="h-3.5 w-3.5 text-gray-400" />}
              label="Name"
              value={member?.emergencyContactName || null}
              testId="text-sidebar-ec-name"
            />
            <SidebarRow
              icon={<Phone className="h-3.5 w-3.5 text-gray-400" />}
              label="Phone"
              value={member?.emergencyContactPhone || null}
              testId="text-sidebar-ec-phone"
            />
            <SidebarRow
              icon={<User className="h-3.5 w-3.5 text-gray-400" />}
              label="Relationship"
              value={member?.emergencyContactRelationship || null}
              testId="text-sidebar-ec-relationship"
            />
          </SidebarSection>

          <Link
            href={`/app/members/${memberId}`}
            className="mt-1 flex items-center justify-center gap-1.5 rounded-md border border-gray-200 px-3 py-2 text-xs font-medium text-blue-600 hover:bg-blue-50"
            data-testid="link-sidebar-full-profile-bottom"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open full profile to edit
          </Link>
        </div>
      )}
      </aside>
    </>
  );
}
